import { CareerSkillGraphBoardQuery } from 'core/graphql';
import {
  EMPTY_SKILL_PROGRESS,
  ISkillProgress,
  RequirementStatus,
  toRequirementStatus,
  transformSkillProgress,
} from 'core/hooks/Growth/transform/transformSkillProgress';

/**
 * 🔴 Объединения объявлены ТИПАМИ, а не свободной строкой. Восьмой статус
 * узла ломает компиляцию в месте использования — и человек вспоминает про
 * `EXPLICIT_KEYS` в locale-тесте ДО релиза, а не после. Свободная строка
 * молча уехала бы студенту сырым ключом прямо на карту.
 */
export type CareerSkillStatus =
  | 'confirmed'
  | 'declared'
  | 'in_progress'
  | 'missing_critical'
  | 'missing'
  | 'optional'
  | 'hidden';

export type CareerSkillPriority = 'core' | 'important' | 'optional';

export type CareerSkillEdgeKind = 'hierarchy' | 'required' | 'recommended';

/** '' — карта полная. Остальные два объясняют, ЧЕМ она урезана. */
export type CareerGraphFallback = '' | 'no_requirements' | 'no_skills';

const STATUSES: CareerSkillStatus[] = [
  'confirmed',
  'declared',
  'in_progress',
  'missing_critical',
  'missing',
  'optional',
  'hidden',
];

const EDGE_KINDS: CareerSkillEdgeKind[] = ['hierarchy', 'required', 'recommended'];

export interface ICareerSkillCluster {
  code: string;
  title: string;
  description: string;
  icon: string;
  order: number;
}

export interface ICareerSkillDemand {
  liveVacancies: number;
  rank: number;
  band: number;
  computedAt: string;
}

export interface ICareerSkillUpcoming {
  lessonNumber: number;
  lessonsLeft: number;
}

export interface ICareerSkillProvider {
  courseId: string;
  courseName: string;
  courseUrl: string;
  level: number;
  completed: boolean;
}

export interface ICareerSkillNode {
  /** Непрозрачный идентификатор с префиксом. 🔴 Разбирать его нельзя — переход
   * строится по `slug`. */
  id: string;
  slug: string;
  name: string;
  description: string;
  iconUrl: string;
  /** Категория справочника: ею красится буквенная плашка, когда знака нет. */
  category: string;
  clusterCode: string;
  requiredLevel: number;
  priority: CareerSkillPriority;
  status: CareerSkillStatus;
  /** ТЗ 0071: положение относительно требования — отдельно от статуса узла,
   * где `in_progress` склеивает «уровень ниже» и «откроется на курсе». */
  requirementStatus: RequirementStatus;
  /** `null` — навыка нет вовсе. Ноль означал бы «есть, но нулевого уровня», а
   * такого уровня не существует. */
  myLevel: number | null;
  source: string;
  demand: ICareerSkillDemand | null;
  upcoming: ICareerSkillUpcoming | null;
  providers: ICareerSkillProvider[];
}

export interface ICareerSkillEdge {
  from: string;
  to: string;
  kind: CareerSkillEdgeKind;
}

/**
 * ТЗ 0071: что студенту делать с курсом — от этого зависит подпись кнопки
 * («Kontynuuj kurs» / «Rozpocznij kurs» / «Zobacz program kursu»).
 */
export type CareerCourseAccess = 'available' | 'in_progress' | 'purchased_not_started';

/** ТЗ 0071: почему выбран именно этот курс. '' — сервер причину не назвал. */
export type CareerCourseReason = 'closes_critical' | 'closes_important' | '';

export interface ICareerCourseRecommendation {
  courseId: string;
  courseName: string;
  courseUrl: string;
  /** СЛАГИ узлов, которые курс закроет (не идентификаторы узлов). */
  closesNodes: string[];
  criticalGaps: number;
  importantGaps: number;
  careerOutcome: string;
  durationLabel: string;
  status: 'available' | 'enrolled';
  access: CareerCourseAccess;
  /** ИМЕНА пробелов: «закроет три пробела» без имён ТЗ 0071 запрещает. */
  closesSkillNames: string[];
  reasonCode: CareerCourseReason;
}

export interface ICareerSkillGraph {
  trackCode: string;
  trackTitle: string;
  trackIcon: string;
  /** Явного выбора нет — сервер подставил первую активную профессию. */
  trackIsDefault: boolean;
  /** Показана именно выбранная цель, а не чужая карта и не подстановка. */
  isChosenGoal: boolean;
  isFallback: boolean;
  fallbackReason: CareerGraphFallback;
  computedAt: string;
  clusters: ICareerSkillCluster[];
  nodes: ICareerSkillNode[];
  edges: ICareerSkillEdge[];
  recommendedCourses: ICareerCourseRecommendation[];
  /** ТЗ 0071: те же пять чисел, что на главной, в каталоге и в паспорте. */
  skillProgress: ISkillProgress;
}

/**
 * ⚠️ Неизвестный статус вырождается в `missing`, а не роняет экран.
 * Бэкенд может уехать вперёд фронта на один релиз — это норма, а не авария, и
 * узел с непонятным статусом честнее нарисовать «не хватает», чем показать
 * пустую карту.
 */
const toStatus = (value: string): CareerSkillStatus =>
  STATUSES.includes(value as CareerSkillStatus) ? (value as CareerSkillStatus) : 'missing';

const toKind = (value: string): CareerSkillEdgeKind =>
  EDGE_KINDS.includes(value as CareerSkillEdgeKind)
    ? (value as CareerSkillEdgeKind)
    : 'recommended';

/**
 * ⚠️ Неизвестный доступ вырождается в `available`: кнопка «посмотреть
 * программу» безопасна при любом положении дел, а «продолжить» к курсу, который
 * человек не покупал, — обещание, которое страница курса не выполнит.
 */
const toAccess = (value: string | null | undefined): CareerCourseAccess =>
  value === 'in_progress' || value === 'purchased_not_started' ? value : 'available';

const toReason = (value: string | null | undefined): CareerCourseReason =>
  value === 'closes_critical' || value === 'closes_important' ? value : '';

const toPriority = (value: string): CareerSkillPriority =>
  value === 'core' || value === 'optional' ? value : 'important';

export const transformCareerSkillGraph = (
  data?: CareerSkillGraphBoardQuery,
): ICareerSkillGraph | null => {
  const graph = data?.myCareerSkillGraph;
  if (!graph) return null;

  return {
    trackCode: graph.trackCode,
    trackTitle: graph.trackTitle,
    trackIcon: graph.trackIcon,
    trackIsDefault: graph.trackIsDefault,
    isChosenGoal: graph.isChosenGoal,
    isFallback: graph.isFallback,
    fallbackReason: (graph.fallbackReason || '') as CareerGraphFallback,
    computedAt: graph.computedAt || '',
    skillProgress: graph.skillProgress
      ? transformSkillProgress(graph.skillProgress)
      : EMPTY_SKILL_PROGRESS,
    clusters: (graph.clusters || []).map((cluster) => ({
      code: cluster.code,
      title: cluster.title,
      description: cluster.description || '',
      icon: cluster.icon || '',
      order: cluster.order,
    })),
    nodes: (graph.nodes || []).map((node) => ({
      id: node.id,
      slug: node.slug,
      name: node.name,
      description: node.description || '',
      iconUrl: node.iconUrl || '',
      category: node.skill?.category || '',
      clusterCode: node.clusterCode,
      requiredLevel: node.requiredLevel,
      priority: toPriority(node.priority),
      status: toStatus(node.status),
      requirementStatus: toRequirementStatus(node.requirementStatus),
      myLevel: node.myLevel ?? null,
      source: node.source || '',
      demand: node.demand
        ? {
            liveVacancies: node.demand.liveVacancies,
            rank: node.demand.rank,
            band: node.demand.band,
            computedAt: node.demand.computedAt || '',
          }
        : null,
      upcoming: node.upcoming
        ? {
            lessonNumber: node.upcoming.lessonNumber,
            lessonsLeft: node.upcoming.lessonsLeft,
          }
        : null,
      providers: (node.providers || []).map((provider) => ({
        courseId: provider.courseId,
        courseName: provider.courseName,
        courseUrl: provider.courseUrl,
        level: provider.level,
        completed: provider.completed,
      })),
    })),
    edges: (graph.edges || []).map((edge) => ({
      from: edge.from,
      to: edge.to,
      kind: toKind(edge.kind),
    })),
    recommendedCourses: (graph.recommendedCourses || []).map((course) => ({
      courseId: course.courseId,
      courseName: course.courseName,
      courseUrl: course.courseUrl,
      closesNodes: course.closesNodes || [],
      criticalGaps: course.criticalGaps,
      importantGaps: course.importantGaps,
      careerOutcome: course.careerOutcome || '',
      durationLabel: course.durationLabel || '',
      status: course.status === 'enrolled' ? 'enrolled' : 'available',
      access: toAccess(course.access),
      closesSkillNames: course.closesSkillNames || [],
      reasonCode: toReason(course.reasonCode),
    })),
  };
};
